import { useState } from "react";
import { Card, CardContent } from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { Badge } from "@app/components/ui/badge";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@app/components/ui/dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Layers,
  Loader2,
  Upload,
  ChevronRight,
} from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";

export interface OntologyStackConfig {
  id: string;
  title: string;
  comment: string | null;
  deployed_specialite: string | null;
  created_at: string | null;
  field_count?: number;
}

interface OntologyStackManagerProps {
  stacks: OntologyStackConfig[];
  onAdd: (data: { title: string; comment: string | null }) => void;
  onUpdate: (stackId: string, data: { title?: string; comment?: string | null }) => void;
  onDelete: (stackId: string) => void;
  onSelectStack: (stackId: string) => void;
  onDeployToSpecialite?: (stackId: string) => void;
  deployingStackId?: string | null;
  specialite?: string;
  isAdmin?: boolean;
  loading?: boolean;
  readOnly?: boolean;
}

function OntologyStackEditorDialog({
  open,
  onClose,
  onSave,
  initialData,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (data: { title: string; comment: string | null }) => void;
  initialData?: OntologyStackConfig;
}) {
  const { t } = useLingui();
  const [title, setTitle] = useState(initialData?.title || "");
  const [comment, setComment] = useState(initialData?.comment || "");
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    setError(null);
    if (!title.trim()) {
      setError(t`Le nom est requis`);
      return;
    }
    onSave({ title: title.trim(), comment: comment.trim() || null });
    onClose();
  };

  const isEditing = !!initialData;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? <Trans>Modifier la configuration</Trans> : <Trans>Nouvelle configuration d'ontologie</Trans>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="stack-title"><Trans>Nom *</Trans></Label>
            <Input
              id="stack-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex. : Rachis v2, Épaule optimisée..."
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="stack-comment"><Trans>Commentaire</Trans></Label>
            <Input
              id="stack-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Notes internes..."
              className="mt-1"
            />
          </div>
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <Trans>Annuler</Trans>
          </Button>
          <Button onClick={handleSave}>
            {isEditing ? <Trans>Mettre à jour</Trans> : <Trans>Créer</Trans>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OntologyStackCard({
  stack,
  onEdit,
  onDelete,
  onSelect,
  onDeployToSpecialite,
  deploying,
  specialite,
  isAdmin,
  readOnly,
}: {
  stack: OntologyStackConfig;
  onEdit: () => void;
  onDelete: () => void;
  onSelect: () => void;
  onDeployToSpecialite?: () => void;
  deploying?: boolean;
  specialite?: string;
  isAdmin?: boolean;
  readOnly?: boolean;
}) {
  const { t } = useLingui();

  return (
    <Card className="hover:shadow-md hover:border-primary/50 transition-all">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-slate-900 truncate">{stack.title}</h4>
              <Badge variant="outline" className="text-xs">
                <Trans>{stack.field_count ?? 0} champs</Trans>
              </Badge>
              {stack.deployed_specialite && (
                <Badge className="text-xs bg-primary/10 text-primary border-primary/20">
                  ✓ {stack.deployed_specialite}
                </Badge>
              )}
            </div>
            {stack.comment && (
              <p className="text-sm text-slate-500 mt-1 truncate">{stack.comment}</p>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
            {!readOnly && isAdmin && onDeployToSpecialite && specialite && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (
                    confirm(
                      t`Déployer en production pour la spécialité « ${specialite} » ? Les champs et exemples existants seront mis à jour.`,
                    )
                  ) {
                    onDeployToSpecialite();
                  }
                }}
                disabled={deploying}
                className="border-primary/50 text-primary hover:bg-primary/5"
                title={t`Déployer en production`}
              >
                {deploying ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
              </Button>
            )}
            {!readOnly && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onEdit}
                  title={t`Modifier`}
                >
                  <Pencil className="h-4 w-4 text-slate-500" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  title={t`Supprimer`}
                >
                  <Trash2 className="h-4 w-4 text-red-400" />
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={onSelect}
              className="gap-1"
            >
              <Trans>Gérer les champs</Trans>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function OntologyStackManager({
  stacks,
  onAdd,
  onUpdate,
  onDelete,
  onSelectStack,
  onDeployToSpecialite,
  deployingStackId,
  specialite,
  isAdmin,
  readOnly,
}: OntologyStackManagerProps) {
  const { t } = useLingui();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingStack, setEditingStack] = useState<OntologyStackConfig | null>(null);

  const handleDelete = (stackId: string) => {
    if (!confirm(t`Supprimer cette configuration d'ontologie et tous ses champs ?`)) return;
    onDelete(stackId);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-primary" />
          <span className="font-medium text-slate-700">
            <Trans>{stacks.length} configurations d'ontologie</Trans>
          </span>
        </div>
        {!readOnly && (
          <Button
            size="sm"
            onClick={() => {
              setEditingStack(null);
              setEditorOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            <Trans>Nouvelle configuration</Trans>
          </Button>
        )}
      </div>

      {stacks.length === 0 ? (
        <div className="text-center py-12">
          <Layers className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">
            <Trans>Aucune configuration d'ontologie.</Trans>
          </p>
          <p className="text-sm text-slate-400 mt-1">
            <Trans>
              Créez une configuration pour définir les champs et exemples utilisés par les expériences.
            </Trans>
          </p>
          {!readOnly && (
            <Button
              className="mt-4"
              onClick={() => {
                setEditingStack(null);
                setEditorOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              <Trans>Créer une configuration</Trans>
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {stacks.map((stack) => (
            <OntologyStackCard
              key={stack.id}
              stack={stack}
              readOnly={readOnly}
              specialite={specialite}
              isAdmin={isAdmin}
              onEdit={() => {
                setEditingStack(stack);
                setEditorOpen(true);
              }}
              onDelete={() => handleDelete(stack.id)}
              onSelect={() => onSelectStack(stack.id)}
              onDeployToSpecialite={
                onDeployToSpecialite ? () => onDeployToSpecialite(stack.id) : undefined
              }
              deploying={deployingStackId === stack.id}
            />
          ))}
        </div>
      )}

      {editorOpen && (
        <OntologyStackEditorDialog
          open={editorOpen}
          onClose={() => {
            setEditorOpen(false);
            setEditingStack(null);
          }}
          onSave={(data) => {
            if (editingStack) {
              onUpdate(editingStack.id, data);
            } else {
              onAdd(data);
            }
          }}
          initialData={editingStack || undefined}
        />
      )}
    </div>
  );
}
